import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import ImageSlideshow from "lib/components/image/ImageSlideshow";
import ImageView from "lib/components/image/ImageView";
import PictureCaptureView from "lib/components/picture/PictureCaptureView";

import {
	createResource,
	getResourceContent,
	updateResourceContent
} from "resource/ResourceService";

import { ChangeEvent } from "utils/history/Event";
import { compress } from "utils/ImageUtils";
import { asTitle, deleteElement } from "utils/Utils";

import { ImageField } from "../FieldService";
import { FormFieldExpansionProps } from "./FormFieldRow";

export type ImageFormFieldExpansionProps = FormFieldExpansionProps & {
	field: ImageField;
};

export default function ImageFormFieldExpansion(
	props: Readonly<ImageFormFieldExpansionProps>
): JSX.Element {
	const { t } = useTranslation();

	const [images, setImages] = useState<Array<string>>([]);
	const [uploading, setUploading] = useState<number[]>([]);
	const [slideshow, setSlideshow] = useState<number>();

	// We only load the images on the initial render.
	useEffect(() => {
		Promise.all(
			props.field.value.map(id =>
				getResourceContent(id).then(URL.createObjectURL)
			)
		).then(setImages);
	}, []);

	useEffect(() => {
		return () => {
			images.forEach(URL.revokeObjectURL);
		};
	}, [images]);

	function handleValueChange(value: Array<number>) {
		const event = new ChangeEvent("/value", props.field.value, value);
		props.onChange(event);
	}

	async function uploadImage(image: Blob, index: number): Promise<void> {
		setUploading(previous => previous.concat(index));
		const file = await compress(image);

		const data = new FormData();
		data.append("file", file);
		createResource({
			name: file.name,
			mediaType: image.type,
			userRights: []
		})
			.then(location => {
				const id = location.substring(location.lastIndexOf("/") + 1);
				return Number.parseInt(id, 10);
			})
			.then(id =>
				updateResourceContent(id, data).then(() => {
					const newValue = props.field.value.concat(id);
					handleValueChange(newValue);
				})
			)
			.catch(() => {
				throw new Error("failed to create resource");
			})
			.finally(() =>
				setUploading(previous => previous.filter(id => id !== index))
			);
	}

	const changeImage = useCallback(
		(image: Blob): void => {
			setImages(previous => previous.concat(URL.createObjectURL(image)));
			uploadImage(image, images.length);
		},
		[props.onChange, props.field.uuid, props.field.value, images]
	);

	const deleteImage = useCallback(
		(index: number): void => {
			setImages(previous => {
				URL.revokeObjectURL(previous[index]);
				return deleteElement(index, previous);
			});
			const newValue = deleteElement(index, props.field.value);
			handleValueChange(newValue);
		},
		[props.onChange, props.field.uuid, props.field.value]
	);

	return (
		<Grid container direction={"column"} alignItems="flex-start">
			<Grid item container alignItems="center">
				<Grid item xs={6} sm={4} md={3} lg={2}>
					<Typography variant="subtitle2">
						{asTitle(t("default"))}
					</Typography>
				</Grid>
				<Grid item xs={6} sm={4} md={3} lg={2}>
					<Grid container direction="column" spacing={1}>
						<Grid item>
							{props.editable && (
								<PictureCaptureView onCapture={changeImage} />
							)}
						</Grid>

						<Grid item container spacing={2}>
							{images.map((image, index) => (
								<Grid item key={index}>
									<ImageView
										url={image}
										loading={uploading.includes(index)}
										disabled={!props.editable}
										onDelete={() => deleteImage(index)}
										onClick={() => setSlideshow(index)}
									/>
								</Grid>
							))}
						</Grid>
						{slideshow !== undefined && (
							<ImageSlideshow
								images={images}
								index={slideshow}
								onClose={() => setSlideshow(undefined)}
							/>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
